import { useState } from "react";
import { FactoryLead } from "@/mocks/factoryDashboard";

interface Props {
  leads: FactoryLead[];
  onUpdateStatus: (id: string, status: FactoryLead["status"]) => void;
}

const statusColors: Record<string, string> = {
  new: "bg-teal-100 text-teal-700",
  quoted: "bg-orange-100 text-orange-700",
  negotiating: "bg-indigo-100 text-indigo-600",
  closed: "bg-gray-100 text-gray-500",
};

const statusLabels: Record<string, string> = {
  new: "New Lead",
  quoted: "Quote Sent",
  negotiating: "Negotiating",
  closed: "Closed",
};

export default function LeadsPanel({ leads, onUpdateStatus }: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const filtered = filterStatus === "all" ? leads : leads.filter((l) => l.status === filterStatus);

  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              Buyer Requirement Leads
              {newCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 bg-teal-500 text-white text-xs font-bold rounded-full">
                  {newCount}
                </span>
              )}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Incoming buyer requests matched to your product categories</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["all", "new", "quoted", "negotiating", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${filterStatus === s ? "bg-orange-500 text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
            >
              {s === "all" ? "All" : statusLabels[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Leads List */}
      <div className="divide-y divide-gray-50">
        {filtered.map((lead) => (
          <div key={lead.id} className="p-5 hover:bg-gray-50/50 transition-colors">
            {/* Lead Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${statusColors[lead.status]}`}>
                    {statusLabels[lead.status]}
                  </span>
                  <span className="text-xs text-gray-400">{lead.countryFlag} {lead.country}</span>
                  <span className="text-xs font-bold text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full">
                    {lead.matchScore}% match
                  </span>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">{lead.title}</h3>
                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-box-3-line" /> {lead.quantity}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-money-dollar-circle-line" /> {lead.budget}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" /> Delivery: {lead.targetDelivery}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-price-tag-3-line" /> {lead.category}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={expandedId === lead.id ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                  {expandedId === lead.id ? "Collapse" : "View Details"}
                </button>
                {lead.status === "new" && (
                  <button
                    onClick={() => onUpdateStatus(lead.id, "quoted")}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line" /> Send Quote
                  </button>
                )}
                {lead.status === "quoted" && (
                  <button
                    onClick={() => onUpdateStatus(lead.id, "negotiating")}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-500 text-white text-xs font-semibold rounded-lg hover:bg-indigo-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-discuss-line" /> Negotiate
                  </button>
                )}
              </div>
            </div>

            {/* Expanded Details */}
            {expandedId === lead.id && (
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Requirement Details</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{lead.description}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">Buyer Contact</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200">
                        <i className="ri-user-line text-gray-500 text-sm" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Name</p>
                        <p className="text-sm font-semibold text-gray-900">{lead.buyerName}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200">
                        <i className="ri-building-line text-gray-500 text-sm" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Company</p>
                        <p className="text-sm font-semibold text-gray-900">{lead.company}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200">
                        <i className="ri-mail-line text-gray-500 text-sm" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Email</p>
                        <a href={`mailto:${lead.email}`} className="text-sm font-semibold text-orange-500 hover:underline">{lead.email}</a>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200">
                        <i className="ri-whatsapp-line text-gray-500 text-sm" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">WhatsApp</p>
                        <p className="text-sm font-semibold text-gray-900">{lead.whatsapp}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <a
                      href={`mailto:${lead.email}`}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-mail-send-line" /> Email Buyer
                    </a>
                    <a
                      href={`https://wa.me/${lead.whatsapp.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-500 text-white text-xs font-semibold rounded-lg hover:bg-green-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-whatsapp-line" /> WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="py-16 text-center">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-gray-50 rounded-full">
              <i className="ri-mail-open-line text-2xl text-gray-300" />
            </div>
            <p className="text-gray-400 text-sm">No leads found</p>
          </div>
        )}
      </div>
    </div>
  );
}
